import { Check, FilePlus2, FileText, Loader2, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { WorkflowStepDto } from '@/features/workflows/schemas/workflowSchemas';
import { cn } from '@/lib/utils';

import { useTemplates } from '../api/useTemplates';
import { parseTemplateStepSettings, writeTemplateStepSettings } from '../lib/templateStepSettings';

interface TemplateStepPanelProps {
  step: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
  readOnly?: boolean;
  /** Whether the workflow has edits that have not been persisted yet. */
  isDirty?: boolean;
  /** Persists the workflow. Called before leaving for the designer. */
  onSave?: () => void;
}

/**
 * What a Document Template step shows instead of the Method / Risk / Return tabs.
 *
 * One template, one step, one workflow. Multi-document workflows were considered and
 * deliberately left out of v1 — see .claude/docs/template-feature-plan.md.
 */
export function TemplateStepPanel({
  step,
  onUpdate,
  readOnly = false,
  isDirty = false,
  onSave,
}: TemplateStepPanelProps) {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId: string }>();

  const settings = parseTemplateStepSettings(step.settings);
  const { data: templates, isLoading, error } = useTemplates();

  // Set while the author is swapping the attached template for another. Switching
  // must not require detaching first — that loses the link if you change your mind.
  const [isPicking, setIsPicking] = useState(false);

  // The name comes from the library rather than the step's settings: renaming a
  // template in the designer would otherwise leave every workflow showing the old one.
  const attached = templates?.find((t) => t.templateId === settings.templateId);
  const attachedName = attached?.templateName ?? settings.templateName ?? 'Untitled Template';

  function attachTemplate(templateId: string, templateName: string) {
    onUpdate({ settings: writeTemplateStepSettings(step.settings, { templateId, templateName }) });
    setIsPicking(false);
  }

  function detachTemplate() {
    onUpdate({
      settings: writeTemplateStepSettings(step.settings, {
        templateId: null,
        templateName: null,
        fieldBindings: [],
      }),
    });
  }

  function openDesigner(templateId?: string | null) {
    if (!workflowId) return;

    // The designer is a separate route, so leaving unmounts the editor and takes
    // every unsaved edit with it — including the step just added to get here.
    // Persist first; the request is not tied to the component's lifetime.
    if (isDirty) onSave?.();

    // Carry the step id: on save the designer echoes it back so the template lands on
    // THIS step, not on whichever Document Template step happens to come first.
    const base = templateId
      ? `/workflows/${workflowId}/template/${templateId}`
      : `/workflows/${workflowId}/template`;

    navigate(`${base}?step=${encodeURIComponent(step.id)}`);
  }

  const showPicker = (!settings.templateId || isPicking) && (templates?.length ?? 0) > 0;

  return (
    <div className="space-y-4 pt-1">
      <div>
        <h3 className="text-base font-semibold tracking-tight text-black-logo">Template</h3>
        <p className="mt-1 text-sm text-gray66">
          The document this workflow generates — its merge fields, and where each signature goes.
        </p>
      </div>

      {settings.templateId && !isPicking ? (
        <div className="flex items-center gap-3 rounded-lg border border-grayEF p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
          </span>

          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-black-logo">
              {attachedName}
            </span>
            <span className="block text-xs text-gray66">
              {attached
                ? `${attached.fieldCount} field${attached.fieldCount === 1 ? '' : 's'} · ${attached.signerCount} signer${attached.signerCount === 1 ? '' : 's'}`
                : isLoading
                  ? 'Loading…'
                  : 'No longer in your template library.'}
            </span>
          </span>

          {!readOnly && (
            <>
              {/* Only worth offering when there is something to switch TO. */}
              {(templates?.length ?? 0) > 1 && (
                <button
                  type="button"
                  onClick={() => setIsPicking(true)}
                  className="rounded-lg border border-grayEF px-3 py-2 text-sm font-medium text-gray30 transition hover:border-primary hover:text-primary"
                >
                  Change
                </button>
              )}
              <button
                type="button"
                onClick={() => openDesigner(settings.templateId)}
                className="flex items-center gap-1.5 rounded-lg border border-grayEF px-3 py-2 text-sm font-medium text-gray30 transition hover:border-primary hover:text-primary"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Edit
              </button>
              <button
                type="button"
                onClick={detachTemplate}
                title="Detach this template from the step"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray66 transition hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      ) : null}

      {isLoading && !settings.templateId ? (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-grayEF px-4 py-8">
          <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
          <span className="text-sm text-gray66">Loading your templates…</span>
        </div>
      ) : null}

      {showPicker ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-gray30">
              {isPicking
                ? 'Choose a different template'
                : `Choose from your ${templates?.length} template${templates?.length === 1 ? '' : 's'}`}
            </p>
            {isPicking ? (
              <button
                type="button"
                onClick={() => setIsPicking(false)}
                className="text-xs font-medium text-gray66 transition hover:text-primary"
              >
                Cancel
              </button>
            ) : null}
          </div>

          <ul className="max-h-64 space-y-1.5 overflow-y-auto">
            {(templates ?? []).map((template) => {
              const isCurrent = template.templateId === settings.templateId;

              return (
                <li key={template.templateId}>
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => attachTemplate(template.templateId, template.templateName)}
                    className={cn(
                      'group flex w-full items-center gap-3 rounded-lg border p-3 text-left transition',
                      'hover:border-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60',
                      isCurrent ? 'border-primary bg-primary/5' : 'border-grayEF',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        isCurrent ? 'bg-primary/10' : 'bg-grayEF',
                      )}
                    >
                      <FileText
                        className={cn('h-4 w-4', isCurrent ? 'text-primary' : 'text-gray30')}
                        aria-hidden="true"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-black-logo">
                        {template.templateName}
                      </span>
                      <span className="block text-xs text-gray66">
                        {template.fieldCount} field{template.fieldCount === 1 ? '' : 's'} ·{' '}
                        {template.signerCount} signer{template.signerCount === 1 ? '' : 's'}
                      </span>
                    </span>
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0 text-primary transition',
                        isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                      )}
                      aria-hidden="true"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {error ? (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error instanceof Error ? error.message : "Couldn't load your templates."}
        </p>
      ) : null}

      {/* Never hidden. Having templates already is not a reason to stop being able to
          author a new one — that was the old behaviour and it left authors stuck with
          whatever was already in the library. */}
      {!readOnly ? (
        <button
          type="button"
          disabled={!workflowId}
          onClick={() => openDesigner()}
          className={cn(
            'flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
            settings.templateId
              ? 'border border-grayEF text-gray30 hover:border-primary hover:text-primary'
              : 'bg-primary text-white hover:bg-primary/90',
          )}
        >
          <FilePlus2 className="h-3.5 w-3.5" aria-hidden="true" />
          Configure new template
        </button>
      ) : null}
    </div>
  );
}
